/**
 * VrWristMenu — the quick menu on the MOVE hand (XR P2 "VR UI / UX", 2026-09-28).
 *
 * Why the move hand: it is the non-pointing hand (the other one turns and points), so the menu is held up by the hand
 * that is not needed to press it — the watch-check convention Meta's hand guidance and every wrist UI follow — and
 * raising it to look stops you walking, which is exactly when a menu is safe to read. The side follows the comfort
 * model's `moveHand`, so a left-handed player who swaps the sticks swaps the menu with them.
 *
 * Opening is the WATCH CHECK (lib/xr_ui_input `readWrist` + `WristGate`): the back of the wrist turned toward the
 * eyes AND the eyes on it, within reach, held a quarter second — the gate must be released (the wrist turned away)
 * before it can open again, so a dismissed menu does not reopen under a still-raised arm. Once open the menu is
 * STATIC: it stays where it appeared (rig-anchored, never glued to the moving hand — Meta: "spawn at the wrist, then
 * static"), so the other hand can press it without chasing it. It closes on Close, on any item (the world panel takes
 * over), when you look away from it for 1.5 s, or when you move 1.2 m from it.
 *
 * It is pressed by the other hand's RAY (controller trigger, a pinch) or, with tracked hands, by a fingertip POKE.
 * The top strip is the COMBAT HUD: health, the weapon in hand, its magazine or capacitor (the desktop combat HUD's own
 * arithmetic, lib/xr_ui_data `ammoReadout`).
 */

import { VrCanvasPanel } from './VrCanvasPanel.js';
import { col, colA, font, slab, icon, text, bar, levelColor } from './vr_draw.js';
import { WRIST, WRIST_FONT, WRIST_ITEMS, WRIST_LAYOUT, wristWidgets, hitWidget, uvToCanvas, type Widget } from '../../lib/xr_ui_layout.js';
import {
  WristGate, wristMenuPose, pointOnRect, pokeStep, sub, len, dot, norm, type PokeState, type V3, type WristReading,
} from '../../lib/xr_ui_input.js';
import type { AmmoReadout } from '../../lib/xr_ui_data.js';

export interface WristData { hp: number; maxHp: number; weapon: string; ammo: AmmoReadout; time: string }

const LOOK_AWAY_S = 1.5;
const WALK_AWAY_M = 1.2;

export class VrWristMenu {
  readonly surface = new VrCanvasPanel('vr-wrist-menu', WRIST.wM, WRIST.hM, WRIST.cw, WRIST.ch, 1000);
  hand: 'left' | 'right' = 'left';
  private readonly gate = new WristGate();
  private readonly widgets: Widget[] = wristWidgets();
  private readonly pokes = new Map<number, PokeState>();
  private awayS = 0;
  private theme = '';
  opens = 0;
  closes = 0;
  pokeFires = 0;
  lastWhy = '';
  lastReading: WristReading | null = null;

  get open(): boolean { return this.surface.visible; }

  /** Feed the menu hand's watch-check reading (null when that hand is not tracked). Returns what happened. */
  /** xr-ui (2026-09-28): the reading's source changed (grip ↔ wrist joint) — the release rule must not carry over: a
   *  controller left raised after an open would otherwise keep a hand's first watch check from ever opening. */
  resetGate(): void { this.gate.reset(); }

  update(dt: number, reading: WristReading | null, head: V3, headFwd: V3, hovered: boolean): 'opened' | 'closed' | null {
    this.lastReading = reading;
    const fire = this.gate.update(reading, dt);
    if (!this.open) {
      if (fire && reading) { this.openAt(reading.anchor, head); return 'opened'; }
      return null;
    }
    const toMenu = sub(this.surface.pos(), head);
    const gaze = dot(norm(headFwd), norm(toMenu));
    this.awayS = !hovered && gaze < 0.5 ? this.awayS + dt : 0;
    if (this.awayS > LOOK_AWAY_S) { this.close('looked away'); return 'closed'; }
    if (len(toMenu) > WALK_AWAY_M) { this.close('moved away'); return 'closed'; }
    return null;
  }

  /** Appear at the wrist (rig-local), facing the eyes, and stay there. */
  openAt(anchor: V3, head: V3): void {
    const { pos, rot } = wristMenuPose(anchor, head);
    this.surface.setPose(pos, rot);
    this.surface.visible = true;
    this.surface.invalidate();
    this.awayS = 0;
    this.pokes.clear();
    this.opens++;
  }

  close(why: string): void {
    if (!this.surface.visible) return;
    this.surface.visible = false;
    this.closes++;
    this.lastWhy = why;
    this.pokes.clear();
  }

  /** A rig-local ray: `wrist:<widget>` (or `wrist:` over the body). */
  hit(o: V3, d: V3): { target: string; t: number; point: V3 } | null {
    const h = this.surface.hit(o, d);
    if (!h) return null;
    const w = hitWidget(this.widgets, h.px, h.py);
    return { target: `wrist:${w?.id ?? ''}`, t: h.t, point: h.point };
  }

  /** A tracked fingertip against the menu (POKE): the widget it hovers (≤ 5 cm in front) and the one it pressed. */
  poke(slot: number, tip: V3): { hover: string | null; fire: string | null } {
    if (!this.surface.visible) return { hover: null, fire: null };
    const r = pointOnRect(tip, this.surface.pos(), this.surface.rot(), this.surface.wM, this.surface.hM);
    const inside = r.u >= 0 && r.u <= 1 && r.v >= 0 && r.v <= 1;
    let on: string | null = null;
    if (inside && r.d < 0.05 && r.d > -0.03) {
      const { px, py } = uvToCanvas(r.u, r.v, this.surface.cw, this.surface.ch);
      on = hitWidget(this.widgets, px, py)?.id ?? null;
    }
    const st = this.pokes.get(slot) ?? { armed: false };
    const res = pokeStep(st, inside ? r.d : 1, !!on);
    this.pokes.set(slot, res.state);
    if (res.fire && on) this.pokeFires++;
    return { hover: on, fire: res.fire ? on : null };
  }

  /** Repaint when the combat strip or the hover moved. */
  tick(hovers: Set<string>, d: WristData): void {
    if (!this.surface.visible) return;
    const hov: string[] = [];
    for (const h of hovers) if (h.startsWith('wrist:') && h.length > 6) hov.push(h.slice(6));
    hov.sort();
    const theme = document.documentElement.dataset.axTheme ?? '';
    if (theme !== this.theme) { this.theme = theme; this.surface.invalidate(); }
    const hs = new Set(hov);
    this.surface.paint(`${d.hp}|${d.maxHp}|${d.weapon}|${d.ammo.text}|${d.time}|${hov.join(',')}|${theme}|${this.hand}`, (g) => this.draw(g, d, hs));
  }

  private draw(g: CanvasRenderingContext2D, d: WristData, hov: Set<string>): void {
    const W = WRIST.cw, H = WRIST.ch;
    slab(g, { x: 3, y: 3, w: W - 6, h: H - 6 }, 30, colA('--ax-bg', 0.95, '#120d0a'), col('--ax-line2', '#6b5a48'), 4);
    // The combat strip.
    const S = WRIST_LAYOUT.hud;
    slab(g, S, 16, col('--ax-bg2', '#1c1510'), col('--ax-line', '#4b3e31'), 2);
    const hpFrac = d.maxHp > 0 ? d.hp / d.maxHp : 0;
    const hpLevel = hpFrac > 0.5 ? 'good' : hpFrac > 0.25 ? 'warn' : 'bad';
    text(g, 'HP', S.x + 18, S.y + 42, col('--ax-muted', '#8c7a66'), font(WRIST_FONT.label, 'body', 700));
    bar(g, { x: S.x + 70, y: S.y + 20, w: 330, h: 26 }, hpFrac, levelColor(hpLevel), colA('--ax-text', 0.12, '#efe6d2'));
    text(g, `${d.hp} / ${d.maxHp}`, S.x + S.w - 18, S.y + 42, col('--ax-hi', '#ffffff'), font(WRIST_FONT.hud, 'mono', 600), 170, 'right');
    icon(g, 'star', S.x + 16, S.y + 70, 34, col('--ax-accent', '#d9a441'));
    text(g, d.weapon, S.x + 62, S.y + 98, col('--ax-text', '#efe6d2'), font(WRIST_FONT.hud, 'body', 700), 250);
    const ammoColor = d.ammo.kind === 'none' ? col('--ax-muted', '#8c7a66') : d.ammo.fed ? col('--ax-hi', '#ffffff') : col('--ax-warn', '#e6b95a');
    text(g, d.ammo.text || '—', S.x + S.w - 18, S.y + 98, ammoColor, font(WRIST_FONT.hud, 'mono', 600), 260, 'right');
    if (d.ammo.kind !== 'none') bar(g, { x: S.x + 330, y: S.y + 108, w: S.w - 348, h: 8 }, d.ammo.frac, d.ammo.fed ? col('--ax-info', '#6db3f2') : col('--ax-warn', '#e6b95a'), colA('--ax-text', 0.12, '#efe6d2'));
    // The buttons.
    this.widgets.forEach((w, i) => {
      const it = WRIST_ITEMS[i];
      const h = hov.has(w.id);
      const close = it.id === 'close';
      slab(g, w.rect, 18, h ? col('--ax-sel', '#3b2c1f') : col('--ax-bg3', '#2a211a'), h ? col('--ax-hi', '#ffffff') : col('--ax-line2', '#6b5a48'), h ? 4 : 2);
      const ic = 44;
      icon(g, it.icon, w.rect.x + (w.rect.w - ic) / 2, w.rect.y + 14, ic, close ? col('--ax-bad', '#e0604a') : h ? col('--ax-hi', '#ffffff') : col('--ax-accent', '#d9a441'));
      text(g, it.label, w.rect.x + w.rect.w / 2, w.rect.y + w.rect.h - 16, h ? col('--ax-hi', '#ffffff') : col('--ax-text', '#efe6d2'), font(WRIST_FONT.label, 'body', 600), w.rect.w - 8, 'center');
    });
    text(g, d.time, W / 2, H - 16, col('--ax-muted', '#8c7a66'), font(WRIST_FONT.small, 'mono', 500), 200, 'center');
  }

  snapshot(): Record<string, unknown> {
    const r = this.lastReading;
    return {
      open: this.open, hand: this.hand, opens: this.opens, closes: this.closes, lastWhy: this.lastWhy, pokeFires: this.pokeFires,
      paints: this.surface.paints, pos: this.surface.pos(), rot: this.surface.rot(),
      reading: r ? { facing: +r.facing.toFixed(3), gaze: +r.gaze.toFixed(3), dist: +r.dist.toFixed(3), along: r.along === undefined ? null : +r.along.toFixed(3), anchor: r.anchor } : null,
      widgets: this.widgets.map((w) => ({ id: w.id, rect: w.rect })),
    };
  }

  dispose(): void { this.surface.dispose(); }
}
